import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ShoppingBag, Heart, User, X, Plus, Minus,
  Star, Truck, RefreshCw, Shield, Check,
  ChevronRight, ArrowRight, MapPin, CreditCard,
  Smartphone, Banknote, SlidersHorizontal,
  PackageCheck, Sparkles,
} from "lucide-react";

// ─── Types & Interfaces ──────────────────────────────────────────────────
interface Product {
  id: number;
  emoji: string;
  brand: string;
  name: string;
  price: number;
  original: number;
  rating: number;
  reviews: number;
  badge?: string;
  badgeColor?: string;
  colors: string[];
  category: string;
}

interface CartItem {
  key: string;
  product: Product;
  size: string;
  qty: number;
}

// ─── Brand Colors ──────────────────────────────────────────────────────────
const C = {
  cream:      "#faf8f5",
  blush:      "#f5ede3",
  accent:     "#c8a96e",
  accentDark: "#a8893e",
  border:     "#e8e2da",
  ink:        "#1a1a1a",
  inkLight:   "#555",
  inkMuted:   "#999",
  success:    "#3d7a5c",
  error:      "#c0392b",
};

// ─── Helpers ───────────────────────────────────────────────────────────────
const fmt  = (n: number) => `₹${n.toLocaleString("en-IN")}`;
const disc = (p: number, o: number) => Math.round((1 - p / o) * 100);
const uid  = () => "#VLR-" + Math.floor(10000 + Math.random() * 90000);

// ─── Data ──────────────────────────────────────────────────────────────────
const PRODUCTS: Product[] = [
  { id:1, emoji:"👔", brand:"VELOUR", name:"Premium Linen Formal Shirt",    price:1299, original:2499, rating:4.8, reviews:2340, badge:"40% OFF", badgeColor:C.error,   colors:["#1a1a1a","#fff8e7","#2980b9"], category:"shirts"  },
  { id:2, emoji:"👗", brand:"VELOUR", name:"Elegant Anarkali Kurta Set",     price:2199, original:3999, rating:4.9, reviews:1890, badge:"NEW",    badgeColor:C.success, colors:["#c0392b","#9b59b6","#27ae60"], category:"ethnic"  },
  { id:3, emoji:"🧥", brand:"VELOUR", name:"Quilted Oversized Jacket",       price:3499, original:5999, rating:4.7, reviews:987,  badge:"HOT",    badgeColor:C.accent,  colors:["#1a1a1a","#4a4a4a","#8B4513"], category:"jackets" },
  { id:4, emoji:"👖", brand:"VELOUR", name:"Slim Fit Stretch Chinos",        price:1499, original:2499, rating:4.6, reviews:3210, badge:"40% OFF", badgeColor:C.error,   colors:["#2c3e50","#8B4513","#808080"], category:"denim"   },
  { id:5, emoji:"👕", brand:"VELOUR", name:"Premium Pima Cotton Tee",        price:699,  original:1299, rating:4.5, reviews:5670, badge:"SALE",   badgeColor:C.error,   colors:["#fff","#1a1a1a","#c0392b"],    category:"tshirts" },
  { id:6, emoji:"🩱", brand:"VELOUR", name:"Silk Blend Saree Blouse",        price:899,  original:1599, rating:4.7, reviews:1234, badge:"NEW",    badgeColor:C.success, colors:["#c8a96e","#c0392b","#fff"],    category:"ethnic"  },
  { id:7, emoji:"🧣", brand:"VELOUR", name:"Merino Wool Stole",              price:2499, original:3999, rating:4.9, reviews:678,  badge:"HOT",    badgeColor:C.accent,  colors:["#c8a96e","#8B4513","#1a1a1a"], category:"jackets" },
  { id:8, emoji:"🥻", brand:"VELOUR", name:"Handwoven Silk Lehenga",         price:5999, original:9999, rating:4.8, reviews:456,  badge:"40% OFF", badgeColor:C.error,  colors:["#1a1a1a","#8B4513"],           category:"ethnic"  },
];

const CATEGORIES = [
  { key:"all",     icon:"🛍", label:"All",       count:"240+ styles" },
  { key:"shirts",  icon:"👔", label:"Shirts",    count:"120+ styles" },
  { key:"tshirts", icon:"👕", label:"T-Shirts",  count:"180+ styles" },
  { key:"ethnic",  icon:"👗", label:"Ethnic",    count:"320+ styles" },
  { key:"denim",   icon:"👖", label:"Denim",     count:"95+ styles"  },
  { key:"jackets", icon:"🧥", label:"Outerwear", count:"78+ styles"  },
];

const SIZES = ["XS","S","M","L","XL","XXL"];
const OUT_OF_STOCK = ["XXL"];
const FILTERS = ["All","Under ₹999","New Arrivals","Best Sellers","Premium","On Sale"];

const PAYMENT_OPTS = [
  { icon: <Smartphone size={22} />, label:"UPI", desc:"Google Pay, PhonePe, Paytm" },
  { icon: <CreditCard size={22} />, label:"Credit / Debit Card", desc:"Visa, Mastercard, RuPay" },
  { icon: <Banknote size={22} />, label:"Net Banking", desc:"All major Indian banks" },
  { icon: <PackageCheck size={22} />, label:"Cash on Delivery", desc:"Pay when your order arrives" },
];

// ─── Sub-components ────────────────────────────────────────────────────────

const Toast = ({ msg, show }: { msg: string; show: boolean }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className="fixed bottom-8 right-8 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-white text-sm font-medium"
        style={{ background: C.success, maxWidth: 320 }}
      >
        <Check size={18} /> {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

const StarRating = ({ rating, reviews }: { rating: number; reviews: number }) => (
  <div className="flex items-center gap-1.5">
    <div className="flex items-center gap-0.5" style={{ color: C.accent }}>
      {[...Array(5)].map((_, i) => <Star key={i} size={11} fill={i < Math.floor(rating) ? C.accent : "none"} />)}
    </div>
    <span className="text-xs font-semibold" style={{ color: C.ink }}>{rating}</span>
    <span className="text-xs" style={{ color: C.inkMuted }}>({reviews.toLocaleString("en-IN")})</span>
  </div>
);

const ColorDots = ({ colors }: { colors: string[] }) => {
  const [active, setActive] = useState(0);
  return (
    <div className="flex gap-1.5 mt-2.5">
      {colors.map((c, i) => (
        <button key={i} onClick={(e) => { e.stopPropagation(); setActive(i); }}
          className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${i === active ? "scale-110 border-stone-800" : "border-transparent"}`}
          style={{ background: c, outline: "1.5px solid rgba(0,0,0,0.12)", outlineOffset: 1 }} />
      ))}
    </div>
  );
};

// ─── Main Components ───────────────────────────────────────────────────────

const ProductCard = ({ p, onOpen, onQuickAdd }: { p: Product; onOpen: (p: Product) => void; onQuickAdd: (p: Product) => void }) => {
  const [wishlisted, setWishlisted] = useState(false);
  return (
    <motion.div
      layout
      whileHover={{ y: -6, boxShadow: "0 16px 48px rgba(0,0,0,0.13)" }}
      onClick={() => onOpen(p)}
      className="bg-white rounded-2xl overflow-hidden cursor-pointer border group relative"
      style={{ borderColor: C.border }}
    >
      <div className="relative overflow-hidden flex items-center justify-center h-[300px]" style={{ background: C.blush }}>
        {p.badge && (
          <span className="absolute top-3 left-3 text-white text-[10px] font-bold px-2.5 py-1 rounded-full z-10" style={{ background: p.badgeColor }}>
            {p.badge}
          </span>
        )}
        <div className="text-8xl transition-transform duration-500 group-hover:scale-110">{p.emoji}</div>
        <button
          onClick={(e) => { e.stopPropagation(); setWishlisted(!wishlisted); }}
          className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md z-10"
        >
          <Heart size={16} fill={wishlisted ? C.error : "none"} color={wishlisted ? C.error : C.inkMuted} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onQuickAdd(p); }}
          className="absolute bottom-0 left-0 right-0 py-3 text-sm font-semibold text-white bg-[#1a1a1a] translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-20"
        >
          + Quick Add
        </button>
      </div>
      <div className="p-4">
        <p className="text-[11px] font-bold tracking-widest mb-1" style={{ color: C.accent }}>{p.brand}</p>
        <p className="text-sm font-medium truncate mb-2" style={{ color: C.ink }}>{p.name}</p>
        <StarRating rating={p.rating} reviews={p.reviews} />
        <div className="flex items-center gap-2 mt-2.5">
          <span className="text-base font-bold">{fmt(p.price)}</span>
          <span className="text-xs line-through text-gray-400">{fmt(p.original)}</span>
          <span className="text-xs font-semibold" style={{ color: C.success }}>{disc(p.price, p.original)}% off</span>
        </div>
        <ColorDots colors={p.colors} />
      </div>
    </motion.div>
  );
};

// ─── Main App ──────────────────────────────────────────────────────────────
export default function PremiumStore() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeModal, setModal] = useState<Product | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderOpen, setOrder] = useState(false);
  const [activeCat, setCat] = useState("all");
  const [toast, setToast] = useState({ show: false, msg: "" });
  const [modalQty, setModalQty] = useState(1);
  const [modalSize, setModalSize] = useState("M");
  const [orderStep, setOrderStep] = useState(1);
  const [paymentIdx, setPaymentIdx] = useState(0);

  const showToast = (msg: string) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: "" }), 3000);
  };

  const addToCart = (product: Product, size: string, qty: number) => {
    const key = `${product.id}-${size}`;
    setCart(prev => {
      const existing = prev.find(x => x.key === key);
      if (existing) return prev.map(x => x.key === key ? { ...x, qty: Math.min(10, x.qty + qty) } : x);
      return [...prev, { key, product, size, qty }];
    });
    showToast(`${product.name} added to bag!`);
  };

  const cartTotal = cart.reduce((s, x) => s + x.product.price * x.qty, 0);
  const cartCount = cart.reduce((s, x) => s + x.qty, 0);
  const filtered = activeCat === "all" ? PRODUCTS : PRODUCTS.filter(p => p.category === activeCat);

  return (
    <div className="min-h-screen" style={{ background: C.cream, fontFamily: "sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,600;1,400&display=swap');`}</style>

      {/* Header */}
      <header className="bg-white sticky top-0 z-[1000] border-b px-6 h-16 flex items-center justify-between shadow-sm">
        <div className="text-2xl font-bold tracking-widest" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          VEL<span style={{ color: C.accent }}>O</span>UR
        </div>
        <nav className="hidden md:flex gap-6 text-sm font-medium">
          {["Men", "Women", "Ethnic", "Sale 🔥"].map(n => <a key={n} href="#" className="hover:text-[#c8a96e]">{n}</a>)}
        </nav>
        <div className="flex items-center gap-5">
          <button onClick={() => setCartOpen(true)} className="relative flex flex-col items-center">
            <ShoppingBag size={20} />
            <span className="text-[10px] font-bold">Bag</span>
            {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#c8a96e] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>}
          </button>
          <User size={20} />
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 text-center px-4" style={{ background: "linear-gradient(135deg, #fdf6ee, #ede4d8)" }}>
        <h1 className="text-6xl mb-4 font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Dress with <i style={{ color: C.accent }}>Confidence</i>
        </h1>
        <p className="text-gray-500 max-w-lg mx-auto mb-8">Premium fashion for the modern Indian wardrobe.</p>
        <button className="bg-[#1a1a1a] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#c8a96e] transition-all">Shop Collection</button>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {CATEGORIES.map(c => (
            <div key={c.key} onClick={() => setCat(c.key)} className={`p-4 rounded-2xl text-center cursor-pointer border-2 transition-all ${activeCat === c.key ? 'border-[#c8a96e] bg-white' : 'border-transparent bg-stone-100'}`}>
              <div className="text-3xl mb-1">{c.icon}</div>
              <div className="text-xs font-bold">{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map(p => (
            <ProductCard key={p.id} p={p} onOpen={(p) => { setModal(p); setModalQty(1); }} onQuickAdd={(p) => addToCart(p, "M", 1)} />
          ))}
        </div>
      </section>

      {/* Footer Strip */}
      <div className="bg-white border-y py-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center gap-2"><Truck color={C.accent} /> <p className="font-bold">Free Delivery</p></div>
            <div className="flex flex-col items-center gap-2"><RefreshCw color="#2980b9" /> <p className="font-bold">30-Day Returns</p></div>
            <div className="flex flex-col items-center gap-2"><Shield color={C.success} /> <p className="font-bold">Secure Payment</p></div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {activeModal && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white rounded-3xl overflow-hidden max-w-2xl w-full grid md:grid-cols-2 relative">
                <button onClick={() => setModal(null)} className="absolute top-4 right-4 z-10"><X /></button>
                <div className="bg-stone-100 flex items-center justify-center p-10 text-9xl">{activeModal.emoji}</div>
                <div className="p-8 space-y-4">
                    <h2 className="text-2xl font-bold">{activeModal.name}</h2>
                    <div className="text-xl font-bold text-[#c8a96e]">{fmt(activeModal.price)}</div>
                    <div className="flex gap-2">
                        {SIZES.map(s => (
                            <button key={s} onClick={() => !OUT_OF_STOCK.includes(s) && setModalSize(s)} 
                                className={`w-10 h-10 border rounded-lg text-xs font-bold ${modalSize === s ? 'bg-black text-white' : 'bg-white text-black'} ${OUT_OF_STOCK.includes(s) && 'opacity-30 cursor-not-allowed'}`}>
                                {s}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => { addToCart(activeModal, modalSize, modalQty); setModal(null); }} className="w-full bg-black text-white py-3 rounded-xl font-bold">Add to Bag</button>
                </div>
            </motion.div>
          </div>
        )}

        {cartOpen && (
          <div className="fixed inset-0 z-[3000]">
            <div className="absolute inset-0 bg-black/30" onClick={() => setCartOpen(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl flex flex-col">
              <div className="p-6 border-b flex justify-between items-center">
                <h3 className="text-xl font-bold">Your Bag</h3>
                <X onClick={() => setCartOpen(false)} className="cursor-pointer" />
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {cart.map(item => (
                  <div key={item.key} className="flex gap-4 bg-stone-50 p-4 rounded-2xl">
                    <div className="text-4xl bg-white p-2 rounded-xl">{item.product.emoji}</div>
                    <div className="flex-1">
                        <p className="font-bold text-sm">{item.product.name}</p>
                        <p className="text-xs text-gray-400">Size: {item.size} | Qty: {item.qty}</p>
                    </div>
                    <div className="font-bold">{fmt(item.product.price * item.qty)}</div>
                  </div>
                ))}
              </div>
              <div className="p-6 border-t bg-gray-50">
                <div className="flex justify-between font-bold text-lg mb-4"><span>Total:</span><span>{fmt(cartTotal)}</span></div>
                <button onClick={() => { setCartOpen(false); setOrder(true); }} className="w-full bg-black text-white py-4 rounded-2xl font-bold">Checkout</button>
              </div>
            </motion.div>
          </div>
        )}

        {orderOpen && (
          <div className="fixed inset-0 z-[4000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ y: 50 }} animate={{ y: 0 }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
                <div className="bg-black text-white p-4 text-center font-bold">Checkout Process</div>
                <div className="p-8">
                    {orderStep === 1 ? (
                        <div className="space-y-4">
                            <input placeholder="Full Name" className="w-full border p-3 rounded-xl outline-none focus:border-black" />
                            <input placeholder="Complete Address" className="w-full border p-3 rounded-xl outline-none focus:border-black" />
                            <button onClick={() => setOrderStep(2)} className="w-full bg-black text-white py-3 rounded-xl font-bold">Continue to Payment</button>
                        </div>
                    ) : orderStep === 2 ? (
                        <div className="space-y-3">
                            {PAYMENT_OPTS.map((o, i) => (
                                <div key={i} onClick={() => setPaymentIdx(i)} className={`p-4 border-2 rounded-xl cursor-pointer flex gap-4 ${paymentIdx === i ? 'border-black bg-stone-50' : 'border-gray-100'}`}>
                                    {o.icon} <div><p className="font-bold text-sm">{o.label}</p><p className="text-xs text-gray-400">{o.desc}</p></div>
                                </div>
                            ))}
                            <button onClick={() => setOrderStep(3)} className="w-full bg-[#c8a96e] text-white py-3 mt-4 rounded-xl font-bold">Place Order</button>
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <div className="text-6xl mb-4">🎉</div>
                            <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
                            <p className="text-gray-400 text-sm mb-6">Your order {uid()} is being processed.</p>
                            <button onClick={() => { setOrder(false); setOrderStep(1); setCart([]); }} className="w-full bg-black text-white py-3 rounded-xl font-bold">Continue Shopping</button>
                        </div>
                    )}
                </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Toast show={toast.show} msg={toast.msg} />
    </div>
  );
}